/**
 * Os endereços das páginas de recibo e de ordem, a aba nova que as abre e os
 * textos que as duas escrevem.
 *
 * Papel: aqui ficam os caminhos que os botões de Finanças abrem —
 * `/financas/recibo/lancamento/<id>`,
 * `/financas/recibo/outros-bancos/<favorecidoId>/<AAAA-MM>` e
 * `/financas/ordem-de-pagamento/<favorecidoId>/<AAAA-MM>` —, `abrirEmAbaNova`,
 * que diz se o navegador deixou a janela abrir, e as linhas de texto do recibo
 * e da ordem que não são dado: a referência, a data por extenso e a etiqueta
 * da segunda via.
 *
 * Como usar: o `router.tsx` declara as mesmas rotas, com `:lancamentoId`,
 * `:favorecidoId` e `:competencia` no lugar dos valores; mudar um caminho daqui
 * sem mudar a rota de lá abre uma aba com a página de endereço não encontrado.
 *
 * `abrirEmAbaNova` não passa `noopener`: com ele, o `window.open` devolve
 * `null` mesmo quando a janela abre, e todo clique pareceria bloqueado.
 */
import { MESES_POR_EXTENSO, type MesDoFavorecido } from '@concierge/shared';

/** A marca de `data-impressao` do recibo e da ordem, que o `useImpressao` põe no `<body>`. */
export const MARCA_DO_DOCUMENTO = 'documento-de-financas';

/**
 * O endereço do recibo de um lançamento, o 🧾 da linha de Lançamentos.
 *
 * @param lancamentoId - O lançamento.
 * @returns O caminho da página, a partir da raiz.
 */
export function caminhoDoReciboDoLancamento(lancamentoId: string): string {
  return `/financas/recibo/lancamento/${lancamentoId}`;
}

/**
 * O endereço do recibo da ajuda de custo de um cartão de Outros Bancos.
 *
 * @param mes - O favorecido e a competência, em `AAAA-MM`.
 * @returns O caminho da página, a partir da raiz.
 */
export function caminhoDoReciboDeOutrosBancos(mes: MesDoFavorecido): string {
  return `/financas/recibo/outros-bancos/${mes.favorecidoId}/${mes.competencia}`;
}

/**
 * O endereço da ordem de pagamento de um cartão de Outros Bancos.
 *
 * @param mes - O favorecido e a competência, em `AAAA-MM`.
 * @returns O caminho da página, a partir da raiz.
 */
export function caminhoDaOrdemDePagamento(mes: MesDoFavorecido): string {
  return `/financas/ordem-de-pagamento/${mes.favorecidoId}/${mes.competencia}`;
}

/**
 * Abre um endereço numa aba nova.
 *
 * Quem chama avisa a pessoa quando a resposta é `false`, com a frase da origem
 * para o documento que tentava abrir.
 *
 * @param caminho - O caminho da página, a partir da raiz.
 * @returns Falso quando o navegador bloqueou a janela.
 */
export function abrirEmAbaNova(caminho: string): boolean {
  return window.open(caminho, '_blank') !== null;
}

/**
 * O nome de um mês.
 *
 * @param mes - O mês, de 1 a 12.
 * @returns "Março"; vazio fora do intervalo.
 */
function nomeDoMes(mes: number): string {
  return MESES_POR_EXTENSO[mes - 1] ?? '';
}

/**
 * A linha "Referente a" do recibo e da ordem.
 *
 * @param competencia - O mês do documento, em `AAAA-MM`.
 * @returns `Referente a Março de 2026`.
 */
export function referenteA(competencia: string): string {
  return `Referente a ${nomeDoMes(Number(competencia.slice(5, 7)))} de ${competencia.slice(0, 4)}`;
}

/**
 * A linha da data do recibo e da ordem.
 *
 * @param data - O dia, em `AAAA-MM-DD`.
 * @returns `São Paulo, 05 de Março de 2026`, com o dia em dois dígitos.
 */
export function dataPorExtenso(data: string): string {
  return `São Paulo, ${data.slice(8, 10)} de ${nomeDoMes(Number(data.slice(5, 7)))} de ${data.slice(0, 4)}`;
}

/**
 * A etiqueta da segunda via do recibo.
 *
 * @param pagador - O pagador do recibo.
 * @returns `2ª via — ` e a primeira palavra do pagador, ou `2ª via —
 *   Pagador(a)` com o pagador vazio.
 */
export function etiquetaDaSegundaVia(pagador: string): string {
  return `2ª via — ${pagador.trim().split(' ')[0] || 'Pagador(a)'}`;
}
